import { formatBasisPoints } from '../../runtime/format';
import { WEEKDAY_NAMES_ES } from '../calendar';
import { formatEuroCents } from '../money';
import type { ParteBridgeSeedValue } from './gate';

/** «Vienes de El Parte»: how each seed reads in the band. Ids are never printed; they name what was chosen. */
const LABELS: Readonly<Record<string, string>> = Object.freeze({
  definition_id: 'Promoción', source_definition_id: 'Copia de', new_definition_id: '', expected_version: '',
  mode: 'Modo', preset: 'Tipo', distribution_kind: 'Cómo se aplica', code: 'Código', benefit_kind: 'Beneficio',
  scope_kind: 'Sobre', dish_id: 'Plato', dish_ids: 'Platos', amount_minor: 'Descuento', maximum_discount_minor: 'Máximo por cuenta', minimum_subtotal_minor: 'Mínimo por cuenta', loyalty_tier_id: 'Nivel de tarjeta',
  weekdays: 'Días', total_redemption_limit: 'Tope de usos', valid_from_date: 'Desde', valid_to_date: 'Hasta',
  reason_code: 'Motivo', stock_item_id: 'Ingrediente', stock_item_ids: 'Ingredientes', category_id: 'Categoría', category_ids: 'Categorías',
  option_id: 'Opción', field: 'Campo', diet_code: 'Dieta', preparation_time_minutes: 'Minutos de preparación', is_default: 'Opción de serie',
  position_in_category: 'Posición', inventory_rule: 'Regla de inventario', action: 'Acción', table_ids: 'Mesas', table_session_ids: 'Mesas abiertas',
  cause_code: 'Causa', classification: 'Clasificación', check_id: 'Cuenta', date_from: 'Desde', date_to: 'Hasta', status: 'Estado',
  setting_key: 'Ajuste', value_integer: 'Valor', value_code: 'Valor', value_boolean: 'Valor', state: 'Estado', reward_tier: 'Nivel del cupón',
  review_bucket: 'Reseñas', count: 'Cupones', tier_id: 'Nivel', cashback_bps: 'Cashback', employment_id: 'Persona', employment_ids: 'Personal', severity: 'Importancia',
  topic: 'Tema', review_tag: 'Etiqueta', zone_id: 'Zona', minimum_quantity: 'Mínimo', audience: 'Para', notice_id: '',
});

/**
 * The seeds whose label names a feminine noun («Promoción», «Persona», «Mesas»): the band's participle agrees with it
 * («Promoción elegida», «3 mesas elegidas»). Every other labelled id or list names a masculine noun.
 */
const FEMININE: ReadonlySet<string> = new Set([
  'definition_id', 'category_id', 'category_ids', 'option_id', 'check_id', 'employment_id', 'zone_id',
  'table_ids', 'table_session_ids',
]);

/** «elegido», «elegida», «elegidos», «elegidas»: the participle of a chosen id or list, agreeing with its label. */
const chosen = (key: string, count: number): string => {
  const feminine = FEMININE.has(key);
  if (count === 1) return feminine ? 'una elegida' : 'uno elegido';
  return `${count} ${feminine ? 'elegidas' : 'elegidos'}`;
};

/** The settings a Configuración link names, by key: the band names the setting, never prints its key. */
const SETTING_NAMES: Readonly<Record<string, string>> = Object.freeze({
  'workforce.kpi.ready_to_delivered_threshold_seconds': 'Tiempo de entrega a la mesa',
  'relationship.public_review_url': 'Enlace para reseñas de Google',
  'relationship.cashback_auto_apply': 'Uso automático del cashback',
  'relationship.cashback_ticket_cap_minor': 'Límite de cashback por ticket',
  send_order_flow: 'Pedidos desde la mesa',
  'catalog.photo_style': 'Estilo de las fotos',
});

/** The dish field a Menú link opens, in the band's words. */
const DISH_FIELDS: Readonly<Record<string, string>> = Object.freeze({ recipe: 'la receta', allergens: 'los alérgenos', description: 'la descripción', name: 'el nombre' });

/** The one Panel money format: grouped, a no-break space before «€», a real minus sign («−1,50 €»). */
const money = (minor: number): string => formatEuroCents(minor, { cents: 'nonzero' }) ?? String(minor);

const valueText = (key: string, value: ParteBridgeSeedValue): string => {
  if (Array.isArray(value)) {
    if (key === 'weekdays') return (value as readonly number[]).map((day) => WEEKDAY_NAMES_ES[day]?.long ?? String(day)).join(', ');
    return chosen(key, value.length);
  }
  if (typeof value === 'boolean') return value ? 'sí' : 'no';
  if (typeof value === 'number') {
    if (key.endsWith('_minor')) return money(value);
    if (key === 'cashback_bps') return formatBasisPoints(value);
    return value.toLocaleString('es-ES');
  }
  if (key === 'topic' && value === 'offer_qr_at_seating') return 'Ofrecer el QR al sentar';
  if (key === 'topic' && value === 'offer_dessert_at_clearing') return 'Ofrecer el postre al retirar los platos';
  if (key === 'topic' && value === 'ask_review_at_payment') return 'Pedir la reseña al cobrar';
  if (key === 'setting_key' && Object.hasOwn(SETTING_NAMES, String(value))) return SETTING_NAMES[String(value)]!;
  // «Pedidos desde la mesa»: the two choices by their titles, never the stored code.
  if (key === 'value_code' && value === 'QR') return 'Directo a cocina';
  if (key === 'value_code' && value === 'QR+WAITER') return 'El camarero confirma la mesa';
  if (key === 'field' && Object.hasOwn(DISH_FIELDS, String(value))) return DISH_FIELDS[String(value)]!;
  if (key === 'severity' && value === 'info') return 'Informativo';
  if (key === 'audience' && value === 'floor_team') return 'El equipo de sala';
  if (key === 'source_definition_id') return 'la promoción elegida';
  if (key.endsWith('_id')) return FEMININE.has(key) ? 'elegida' : 'elegido';
  return String(value).replaceAll('_', ' ');
};

type ParteBridgeSeedLine = Readonly<{ key: string; label: string; value: string }>;

/** The seeds the band lists: the ones with a label, in the owner's order. */
export function parteBridgeSeedLines(seed: Readonly<Record<string, ParteBridgeSeedValue>>): readonly ParteBridgeSeedLine[] {
  return Object.entries(seed).flatMap(([key, value]) => {
    const label = LABELS[key];
    return label ? [{ key, label, value: valueText(key, value) }] : [];
  });
}
